import type { Offer } from '@fourthwall/services/dist/models/offer'
import { htmlTextContentMax, isValidVariantPriceValue } from '@fourthwall/utils/dist/validation'
import Yup from '@fourthwall/utils/dist/yup'

export const variantPriceSchema = Yup.object().shape({
  value: Yup.number()
    .required()
    .test(
      'isValidVariantPriceValue',
      'Products must have a minimum price of 0.99 or be free.',
      (value) => isValidVariantPriceValue(value),
    ),
})

export const variantWeightSchema = Yup.object({
  value: Yup.number().positive().required(),
  unit: Yup.string().required(),
})

const variantEmptyWeightSchema = Yup.object({
  value: Yup.number(),
  unit: Yup.string(),
})

export const getDigitalFilesSizeLimitSchema = (sizeExceeded: boolean) =>
  Yup.array().test(
    'totalSizeLimit',
    'Total size of digital files exceeds the maximum allowed size',
    () => !sizeExceeded,
  )

export const digitalItemVariantWeightSchema = Yup.object({
  value: Yup.number().required(),
  unit: Yup.string().required(),
})

const getVariantWeightSchema = (areFlatRatesEnabled: boolean) => {
  return areFlatRatesEnabled ? variantEmptyWeightSchema : variantWeightSchema
}

export const variantSkuSchema = Yup.string().min(4).max(40)

export const productValidatorSchema = ({
  manufacturingService,
  areFlatRatesEnabled,
  isTotalDigitalFilesSizeExceeded,
  isProductCategoryRequired,
}: {
  manufacturingService: Offer.ManufacturingService
  areFlatRatesEnabled: boolean
  isTotalDigitalFilesSizeExceeded: boolean
  isProductCategoryRequired: boolean
}) => {
  return {
    name: Yup.string().max(200).required(),
    description: Yup.string().test(
      'htmlTextContentMax',
      'Must have less than 4000 characters.',
      (value) => (value ? htmlTextContentMax(4000)(value) : true),
    ),
    categoryId: isProductCategoryRequired ? Yup.string().required() : Yup.string(),
    sellingPriceValue: Yup.number().test(
      'isValidVariantPriceValue',
      'Products must have a minimum price of 0.99 or be free.',
      (value) => isValidVariantPriceValue(value),
    ),
    variants: Yup.array().of(
      Yup.object().shape({
        price: variantPriceSchema,
        weight:
          manufacturingService === 'DIGITAL_ITEM'
            ? digitalItemVariantWeightSchema
            : getVariantWeightSchema(areFlatRatesEnabled),
        sku: variantSkuSchema.test(
          'conditionalRequired',
          'This field is required.',
          function (value) {
            if (this.options.context?.values.visualHints?.customSkus) {
              return !!value
            }

            return true
          },
        ),
      }),
    ),
    visualHints: Yup.object().shape({
      customSkus: Yup.bool(),
    }),
    withMembersOnly: Yup.boolean(),
    membersOnlyType: Yup.string().oneOf(['ALL_TIERS', 'SELECTED_TIERS']),
    membersOnlyTierIds: Yup.object().when('membersOnlyType', {
      is: (membersOnlyType: any) => membersOnlyType === 'SELECTED_TIERS',
      then: Yup.object().test(
        'isEmpty',
        'At least one selected tier is required',
        (value) => Object.values(value).filter(Boolean).length > 0,
      ),
    }),
    manufacturingService: Yup.string(),
    bespokeProductId: Yup.string().nullable().optional(),
    digitalItems: getDigitalFilesSizeLimitSchema(isTotalDigitalFilesSizeExceeded),
  }
}
